import { Info } from 'lucide-react'
import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router'
import { AppSidebar } from '@/components/app-sidebar'
import { StoreSlugSync } from '@/components/store-slug-sync'
import { Topbar } from '@/components/topbar/topbar'
import { Button } from '@/components/ui/button'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  isStoreShippingOriginAddressComplete,
  readStoreShippingOriginAddress,
} from '@/features/shipping/helpers/store-shipping-origin-address.helper'
import { useStoreDetails } from '@/features/store-settings/hooks/use-store-details'
import { storesQueryOptions } from '@/hooks/use-stores'
import { setStoreSlug } from '@/services/api'

export const Route = createFileRoute('/$store/_layout')({
  beforeLoad: async ({ context, params }) => {
    try {
      const stores = await context.queryClient.ensureQueryData(storesQueryOptions)

      if (stores.length === 0) {
        throw redirect({
          to: '/onboarding',
        })
      }

      const isValidStore = stores.some((store) => store.slug === params.store)

      if (!isValidStore) {
        const firstStore = stores[0]
        throw redirect({
          to: '/$store',
          params: { store: firstStore.slug },
        })
      }

      setStoreSlug(params.store)
      return { stores }
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status

      if (status === 401 || status === 403) {
        throw redirect({
          to: '/signin',
        })
      }

      throw error
    }
  },
  component: StoreDashboardLayout,
})

function StoreDashboardLayout() {
  const { stores } = Route.useRouteContext()
  const { store } = Route.useParams()
  const { storeDetails, isLoading } = useStoreDetails()
  const storeOriginAddress = readStoreShippingOriginAddress(
    storeDetails?.metadata,
  )
  const hasCompleteOriginAddress = isStoreShippingOriginAddressComplete(
    storeOriginAddress,
  )
  const shouldShowOriginAddressBanner = !isLoading && !hasCompleteOriginAddress

  return (
    <SidebarProvider>
      <StoreSlugSync />
      <AppSidebar initialStores={stores} />
      <SidebarInset className="bg-page">
        <Topbar />
        {shouldShowOriginAddressBanner && (
          <div className="flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-tone-amber-fg sm:px-6">
            <p className="min-w-0 flex-1">
              Preencha o endereço físico da loja nas configurações de frete
              para obter cálculo exato para o cliente.
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" asChild>
                <Link to="/$store/settings" search={{ aba: "frete" }} params={{ store }}>
                  Preencher agora
                </Link>
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex text-amber-700 hover:text-amber-900"
                    aria-label="Informações sobre precisão do frete"
                  >
                    <Info className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  Sem endereço físico completo da loja, o sistema usa frete
                  médio e o valor pode não refletir o custo exato da entrega.
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
        <div className="flex flex-1 flex-col gap-4 p-4 sm:p-6">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
